<template>
  <div id="gold-spot">
    <div class="graph-area">
      <div v-if="loading" class="skeleton-card" style="position: absolute;">
        <div class="box"></div>
      </div>
      <svg :id="goldSpotId" width="100%">
        <defs>
          <pattern id="pattern1" x="10" y="10" width="10" height="10" patternUnits="userSpaceOnUse" />
        </defs>
      </svg>
    </div>
    <div class="period-area">
      <PeriodNavigation
        backgorund="#ffffff"
        activePosition="top"
        data-action="change_gold_period"
        data-assettype="gold"
        data-assetname="spot"
        :arrPeriod="getGoldSpotHistoryPeriod.range"
        :alias="`period`"
        @select-data="choosePeriod"
        :defaultItem="$store.state.gold.goldSpotHistoryCurrentPeriod"
        :slidePerPage="6"
      />
    </div>
    <div class="professional-btn-center">
      <a
        class="ctn-graph-professional"
        data-action="gold_spot_trading_view"
        href="javascript:;"
        @click="callProfessionalGraph"
      >
        <i class="fas fa-chart-bar"></i> กราฟเทคนิค
      </a>
    </div>
  </div>
</template>

<script>
import axios from "axios"
import querystring from "querystring"
import moment from "moment"
import * as d3 from "d3"
import { mapGetters, mapActions } from "vuex"
import { formatDate } from "../../../utils/global"

const PeriodNavigation = () => import("../global/PeriodNavigation")

// Helper Functions
let helpers = {
  TH: d3.timeFormatDefaultLocale({
    decimal: ".",
    thousands: ",",
    grouping: [3],
    currency: ["$", ""],
    dateTime: "%a %b %e %X %Y",
    date: "%d/%m/%Y",
    time: "%H:%M:%S",
    periods: ["AM", "PM"],
    days: ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"],
    shortDays: ["อ", "จ", "อ", "พ", "พฤ", "ศ", "ส"],
    months: [
      "มกราคม",
      "กุมภาพันธ์",
      "มีนาคม",
      "เมษายน",
      "พฤษภาคม",
      "มิถุนายน",
      "กรกฎาคม",
      "สิงหาคม",
      "กันยายน",
      "ตุลาคม",
      "พฤศจิกายน",
      "ธันวาคม"
    ],
    shortMonths: ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."]
  }),
  numberFormat: function(num) {
    var parts = num.toString().split(".")
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    return `$ ${parts.join(".")}`
  },
  announcementFormate: function(num, period) {
    return period == "1D" ? `ประกาศครั้งที่ ${num}` : `ประกาศล่าสุดของวัน`
  },
  changeFormat: change => {
    let priceChange = parseFloat(change).toFixed(2)
    if (priceChange > 0) return `+${priceChange}%`
    return `${priceChange}%`
  },
  //   parseDate: d3.timeParse("%Y-%m-%dT%H:%M:%SZ"),
  parseDate: d3.timeParse("%Y-%m-%d %hh:%mm:%ss"),
  getDate: d3.timeFormat("%d/%m/%y | %H:%M:%S"),
  colorTheme: function(pl) {
    if (pl > 0) return constants.profit
    else if (pl == 0) return constants.stable
    return constants.loss
  },
  iconPriceChange: function(pl) {
    if (pl > 0) return "\uf0d8"
    else if (pl == 0) return "\uf0dc"
    return "\uf0d7"
  }
}

// Constant
let constants = {
  graphMarginPercentage: 0.1,

  // color and styling
  grey: "#B0B0B0",
  profit: "#17c161",
  loss: "#da1616", //da1616
  stable: "#999999",
  graphColor: "#3fc0bf",
  graphColor: "#3fc0bf",
  priceToolipColor: "rgba(63, 192, 191, 1)",

  margin: {
    top: 20,
    left: 0,
    right: 0,
    bottom: 20,
    bgTop: 12
  },

  // offset
  offsetRightY: 70,

  // Focusing
  focusStroke: 1.5,
  lineStroke: 2,
  axisStroke: 2,
  circleRadius: 4,

  // Tooltip
  tooltipWidth: 150,
  tooltipHeight: 60,
  tooltipRealtimePriceWidth: 60,
  tooltipRealtimePriceHeight: 20,

  driftRightMargin: 10,
  driftTopMargin: 22.5,

  // Tick Styling
  tickMoveFactor: 34
}

export default {
  components: {
    PeriodNavigation
  },
  props: {
    goldSpotId: {
      type: String,
      default: "gold-spot-graph-d3-line"
    },
    isRealtime: {
      type: Boolean,
      default: true
    },
    arrData:{
      type: Array,
      default: []
    }
  },
  data() {
    return {
      firstPaint: true,
      loading: false
    }
  },
  watch: {
    async $route() {
      await this.queryData()
    }
  },
  computed: {
    ...mapGetters("gold", ["getGoldSpotHistoryCurrentPeriod", "getGoldSpotHistoryPeriod"]),
    yAxisFormatTitle() {
      switch (this.$store.state.gold.goldSpotHistoryCurrentPeriod) {
        case "5D":
          return helpers.TH.format("%e %b")
        case "1M":
          return helpers.TH.format("%e %b")
        case "3M":
          return helpers.TH.format("%e %b")
        case "6M":
          return helpers.TH.format("%e %b")
        case "1Y":
          return helpers.TH.format("%b %y")
        case "5Y":
          return helpers.TH.format("%Y")
        case "10Y":
          return helpers.TH.format("%Y")
        case "YTD":
          return helpers.TH.format("%e %b")
        default:
          return helpers.TH.format("%e %b %y")
      }
    },
    yAxisFormatTick() {
      switch (this.$store.state.gold.goldSpotHistoryCurrentPeriod) {
        case "5D":
          return 4
        case "1M":
          return 4
        case "6M":
          return 2
        case "YTD":
          return 4
        default:
          return 6
      }
    }
  },
  async created() {
    // const ns = await this.$nextTick()
  },
  async mounted() {
    this.loading = !this.loading
    await this.queryData()
    this.loading = !this.loading
  },
  methods: {
    ...mapActions("gold", ["findGoldSpotHistoryGraph"]),
    async choosePeriod(period) {
      this.$store.state.gold.goldSpotHistoryCurrentPeriod = period
      await this.queryData()
    },
    async queryData() {
      await this.getSnapshot()
      await this.renderGraph()
    },
    async getSnapshot() {
      try {
        if (this.getGoldSpotHistoryCurrentPeriod) {
          let from = this.getGoldSpotHistoryCurrentPeriod
          let to = formatDate(Date.now())
          await this.findGoldSpotHistoryGraph({ from, to })
        }
      } catch (error) {
        throw new Error(error)
      }
    },
    graphHeight() {
      let graphWidth = document.getElementById(this.goldSpotId).clientWidth
      let graphRatio = graphWidth > 760 ? 0.5 : 0.6

      return graphWidth * graphRatio
    },
    checkNavData() {
      return this.arrData.length > 0
    },
    renderGraph() {
      if (!this.checkNavData) {
        return
      }

      if (this.firstPaint) {
        this.drawGraph({ isFirst: true })
        this.firstPaint = false
      } else {
        this.drawGraph({ isFirst: false })
      }
    },
    drawGraph(options) {
      let isFirst = options.isFirst
      // Preparing Data
      let formattedLineData = this.arrData

      let vis = d3.select(`#${this.goldSpotId}`)
      let spaceNavLineElem = document.getElementById(this.goldSpotId)

      // change format date
      formattedLineData.forEach(d => {
        d.d = new Date(d.d)
      })

      // Prepare range and domain
      // Max and min in x bar, y bar
      let svgWidth, svgHeight
      let xRange, yRange
      let xAxis, yAxis
      let graphMargin
      let yMin = d3.min(formattedLineData, d => Number(d.v))
      let yMax = d3.max(formattedLineData, d => Number(d.v))

      //manage text in ticks
      let formatCurrency = d => {
        let toNumber = parseFloat(d)
          .toLocaleString("th", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 4
          })
          .replace(/\B(?=(\d{3})+(?!\d))/g, ",")
        return `$ ${toNumber}`
      }

      let sizeUpdate = () => {
        let clientWidth = document.getElementById(this.goldSpotId).clientWidth
        svgWidth = clientWidth ? clientWidth : 0
        svgHeight = this.graphHeight()
        spaceNavLineElem.style.height = svgHeight + "px"
      }

      let rangeUpdate = () => {
        xRange = d3
          .scaleTime()
          .range([constants.margin.left + 10, svgWidth - constants.margin.right - constants.offsetRightY])
          .domain([formattedLineData[0].d, formattedLineData[formattedLineData.length - 1].d])

        yRange = d3
          .scaleLinear()
          .range([constants.margin.bottom, svgHeight - constants.margin.top])
          .domain([yMax, yMin])
      }

      let axisUpdate = () => {
        xAxis = d3
          .axisBottom(xRange)
          .tickFormat(this.yAxisFormatTitle)
          .ticks(this.yAxisFormatTick)

        yAxis = d3
          .axisRight(yRange)
          .tickFormat(formatCurrency)
          .ticks(4)
      }

      sizeUpdate()
      rangeUpdate()
      axisUpdate()

      // Appending or Changing Element
      let lineGen = d3
        .line()
        .curve(d3.curveLinear) // http://bl.ocks.org/d3indepth/b6d4845973089bc1012dec1674d3aff8
        .x(d => xRange(d.d))
        .y(d => yRange(d.v))

      // y Axis
      var make_y_axis = function() {
        return d3.axisRight(yRange).ticks(4)
      }

      var generateTooltip = () => {
        // Tooltip
        let tooltip = vis
          .append("g")
          .attr("class", "focus-tooltip")
          .style("display", "none")

        // Tooltip background
        tooltip
          .append("rect")
          .attr("class", "tooltip-rect")
          .attr("width", constants.tooltipWidth)
          .attr("height", constants.tooltipHeight)
          .attr("rx", 4)
          .attr("ry", 4)

        // Tooltip date
        tooltip
          .append("text")
          .attr("x", 40)
          .attr("y", 20)
          .attr("class", "tooltip-date")
          .attr("text-anchor", "left")
          .attr("fill", constants.grey)
          .text("12/03/07")

        // Tooltip icon
        tooltip
          .append("text")
          .attr("class", "tooltip-logo")
          .attr("x", 25)
          .attr("y", 20)
          .attr("fill", constants.grey)
          .attr("font-family", '"Font Awesome 5 Pro"')
          .attr("font-size", "10px")
          .text("\uf017")

        // Tooltip nav data
        tooltip
          .append("text")
          .attr("x", 50)
          .attr("y", 45)
          .attr("class", "tooltip-nav")
          .attr("text-anchor", "left")
          .text("10.2464")
      }

      var generatePriceTooltip = () => {
        let tooltip = vis.append("g").attr("class", "price-tooltip")

        tooltip
          .append("rect")
          .attr("class", "price-tooltip-rect")
          .attr("width", constants.tooltipRealtimePriceWidth)
          .attr("height", constants.tooltipRealtimePriceHeight)
          .attr("rx", 1)
          .attr("ry", 1)
          .data(formattedLineData)
          .attr("x", d => xRange(d.d))
          .attr("y", d => yRange(d.v))
          .attr("fill", constants.priceToolipColor)
          .attr("transform", `translate(10,-10)`)

        // Tooltip nav data
        tooltip
          .append("text")
          .attr("class", "price-tooltip-nav")
          .attr("text-anchor", "left")
          .data(formattedLineData)
          .attr("x", d => xRange(d.d))
          .attr("y", d => yRange(d.v))
          .attr("transform", `translate(15,5)`)
          .attr("fill", "white")
          .style("font-size", "10px")
          .text(d => {
            return `$ ${d.v}`
          })
      }

      if (isFirst) {
        // Create x axis
        vis
          .append("svg:g")
          .attr("class", "x axis")
          .call(xAxis)

        // Create y axis
        vis
          .append("svg:g")
          .attr("class", "y axis")
          .attr("transform", `translate(${svgWidth - 90},-10)`)
          .attr("fill", "none")
          .call(yAxis)

        // Create line in graph y axis
        vis
          .append("g")
          .attr("class", "grid")
          .style("opacity", ".2")
          .attr("transform", `translate(-${constants.offsetRightY},0)`)
          .attr("stroke-dasharray", "3,3")
          .call(
            make_y_axis()
              .tickSize(0 + svgWidth, 0, 0)
              .tickFormat("")
          )

        // Data Line
        vis
          .append("path")
          .attr("class", "line-data")
          .transition()
          .duration(100)
          .attr("d", lineGen(formattedLineData))
          .attr("stroke", constants.graphColor)
          .attr("stroke-width", constants.lineStroke)
          .attr("fill", "none")
       
        // focus Tooltip
        generateTooltip()

        // Line on focus
        vis
          .append("line")
          .attr("class", "focus-line")
          .attr("y1", constants.margin.top)
          .attr("y2", svgHeight - constants.margin.bottom)
          .attr("stroke-width", constants.focusStroke)
          .attr("stroke", "#999999")
          .style("display", "none")

        // Circle
        let focusCircle = vis
          .append("g")
          .attr("class", "focus-circle")
          .style("display", "none")

        // Circle Background
        focusCircle.append("circle").attr("r", constants.circleRadius + 3)

        // Circle Inside
        focusCircle
          .append("circle")
          .attr("class", "inner-circle")
          .attr("r", constants.circleRadius)

      } else {
        // X Axis
        vis.select(".x").call(xAxis)

        vis.selectAll(".focus-line").style("display", "none")

        vis.selectAll(".focus-circle").style("display", "none")

        vis.selectAll(".focus-tooltip").style("display", "none")

        // y Axis
        vis
          .select(".y")
          .attr("fill", "none")
          .call(yAxis)

        // grid
        vis
          .select(".grid")
          .attr("class", "grid")
          .style("opacity", ".2")
          .attr("transform", `translate(-${constants.offsetRightY},0)`)
          .call(
            make_y_axis()
              .tickSize(0 + svgWidth, 0, 0)
              .tickFormat("")
          )

        // Data Line
        vis
          .select(".line-data")
          .transition()
          .duration(500)
          .attr("d", lineGen(formattedLineData))
          .attr("stroke", constants.graphColor)
          .attr("stroke-width", constants.lineStroke)
          .attr("fill", "none")
      }

      d3.selectAll(".domain").style("display", "none")

      // Handling mouse event for focus tooltip

      //cache for performance
      let elemFocusLine = vis.select(".focus-line")
      let elemFocusCircle = vis.select(".focus-circle")
      let elemFocusTooltip = vis.select(".focus-tooltip")
      let elemFocusTooltipDate = vis.selectAll(".tooltip-date")
      let elemFocusTooltipNav = vis.selectAll(".tooltip-nav")

      // Helpers function
      let mouseEvent = {
        bisectDate: () =>
          d3.bisector(function(d) {
            return d.d
          }).left,
        mouseOver: () => {
          elemFocusLine.style("display", null)
          elemFocusCircle.style("display", null)
          elemFocusTooltip.style("display", null)
        },
        mouseOut: () => {
          elemFocusLine.style("display", "none")
          elemFocusCircle.style("display", "none")
          elemFocusTooltip.style("display", "none")
        },
        mouseMove: function() {
          mouseEvent.handleMove(d3.mouse(this)[0], d3.mouse(this)[1])
        },
        touchMove: function() {
          mouseEvent.handleMove(d3.touches(this)[0][0], d3.touches(this)[0][1])
        },
        handleMove: (xPos, yPos) => {
          elemFocusLine.style("display", null)
          elemFocusCircle.style("display", null)
          elemFocusTooltip.style("display", null)

          let x0 = xRange.invert(xPos),
            i = mouseEvent.bisectDate()(formattedLineData, x0, 1),
            d0 = formattedLineData[i - 1],
            d1 = formattedLineData[i]

          if (d0 != null && d1 != null) {
            let d = x0 > d0.d ? d1 : d0
            if (xRange(d0.d) == 0 && xRange(x0) < xRange(d1.d) / 2) {
              d = d0
            }

            elemFocusLine.attr("transform", "translate(" + xRange(d.d) + "," + 0 + ")")
            elemFocusCircle
              .attr("transform", "translate(" + xRange(d.d) + "," + yRange(d.v) + ")")
              .attr("fill", constants.graphColor)
            // Tooltip move
            // Modify padding for displaying
            let xPad = 0,
              yPad = 0
            if (svgWidth - xRange(d.d) < constants.tooltipWidth) {
              xPad = -constants.tooltipWidth - constants.driftRightMargin * 2
            }

            if (svgHeight - constants.margin.bottom - constants.margin.bgTop - yRange(d.v) < constants.tooltipHeight) {
              yPad = -constants.tooltipHeight / 2
            }

            if (yRange(d.v) < constants.tooltipHeight) {
              yPad = constants.tooltipHeight / 2
            }

            // set tooltip price format
            elemFocusTooltipNav.text(helpers.numberFormat(d.v))
            // set tooltip date format
            elemFocusTooltipDate.text(helpers.getDate(d.d))

            // elemFocusTooltip.attr("transform", "translate(" + xRange(d.d+xPad) + "," + yRange(d.v+yPad) + ")")

            elemFocusTooltip.attr(
              "transform",
              "translate(" +
                (xRange(d.d) + xPad + constants.driftRightMargin) +
                "," +
                (yRange(d.v) + yPad - constants.driftTopMargin) +
                ")"
            )
          }
        }
      }

      if (!isFirst) {
        vis.select(".focusing-overlay").remove()
      }

      // Overlay Element
      vis
        .append("rect")
        .attr("class", "focusing-overlay")
        .attr("fill", "none")
        .attr("pointer-events", "all")
        .attr("y", constants.margin.bgTop)
        .attr("width", svgWidth)
        .attr("height", svgHeight - constants.margin.bottom - constants.margin.bgTop)
        .on("mouseover", mouseEvent.mouseOver)
        .on("mouseout", mouseEvent.mouseOut)
        .on("mousemove", mouseEvent.mouseMove)
        .on("touchmove", mouseEvent.touchMove, true)

      // Styling
      // First and last tick
      let tickUpdate = () => {
        var fontSize
        if (svgWidth < 768) {
          fontSize = "10px"
        } else {
          fontSize = "14px"
        }
        vis
          .selectAll(".tick text")
          .attr("x", constants.tickMoveFactor)
          .attr("y", 10)
          .attr("font-family", '"DBHeaventRez"')
          .style("font-size", fontSize)
          .attr("fill", constants.grey)

        vis.selectAll(".x .tick text").attr("x", 0)
        // vis.selectAll(".x .tick:nth-of-type(2) text").attr("x", 0);

        vis.selectAll(".x .tick text").attr("y", this.graphHeight() + 10)

        vis.selectAll(".y .tick text").style("font-size", fontSize)

        vis.selectAll(".y").attr("transform", `translate(${svgWidth - 90},-10)`)

        vis.selectAll(".grid").attr("transform", `translate(-${constants.offsetRightY},0)`)

        vis
          .selectAll(".x-axis-line")
          .attr("x1", 0)
          .attr("x2", svgWidth)
          .attr("y1", svgHeight)
          .attr("y2", svgHeight)
          .style("font-size", fontSize)

        vis
          .selectAll(".focus-line")
          .attr("y1", constants.margin.top)
          .attr("y2", svgHeight)

        document.getElementById(this.goldSpotId).style.height = this.graphHeight() + 30 + "px"
      }

      tickUpdate()

      // Inner circle focusing
      vis.select(".inner-circle").attr("fill", "white")

      // Responsive Event for graph and axis
      let updateAxisAndGraph = () => {
        vis.select(".x").call(xAxis)

        vis
          .select(".y")
          .attr("fill", "none")
          .attr("transform", "translate(-20, 0)")
          .call(yAxis)

        // Update Position and Data Line
        vis.select(".line-data").attr("d", lineGen(formattedLineData))

        vis
          .select(".grid")
          .attr("class", "grid")
          .style("opacity", ".2")
          .attr("transform", "translate(50, 0)")
          .call(
            make_y_axis()
              .tickSize(0 + svgWidth, 0, 0)
              .tickFormat("")
          )
      }

      if (!isFirst) {
        d3.select(window).on("resize", null)
      }

      d3.select(window).on("resize", () => {
        sizeUpdate()
        rangeUpdate()
        axisUpdate()
        updateAxisAndGraph()
        tickUpdate()
      })
    },
    callProfessionalGraph() {
      this.$emit("call-tradingview", true)
    }
  }
}
</script>

<style lang="scss">
#gold-spot {
  .graph-area {
    position: relative;
    height: 100%;

    margin: 12px 0;
  }
  .professional-btn-center {
    margin: 20px 0 25px 0;
    text-align: center;
    .ctn-graph-professional {
      @include finRippleBtn(#f4f4f4, #f4f4f4, $color-normal-text);
      @extend %font-size-ss;
      padding: 5px 19px;
      text-decoration: none;
      font-weight: bold;
      border-radius: 4px;
      i {
        pointer-events: none;
        margin-right: 4px;
      }
    }
  }
  .tooltip-rect {
    fill: #ffffff;
    fill-opacity: 1;
    stroke: rgba(186, 186, 186, 0.24);
    stroke-width: 1px;
  }
  .tooltip-nav {
    fill: #444444;
    @extend %font-size-ss;
    font-weight: bold;
    text-anchor: left;
  }
  .tooltip-date {
    fill: #979797;
    @extend %font-size-sss;
  }
  .tooltip-created-at {
    fill: #444444;
    @extend %font-size-sss;
  }
}
</style>